import { Injectable } from '@angular/core';

import { MapsService } from '../../../modules/maps';
import { Observable } from 'rxjs/Observable';
import { JobDomainService } from '../../domain/services/job/job.domain.service';
import { UserDomainService } from '../../domain/services/user/user.domain.service';
import { QuotaDomainService } from '../../domain/services/quota/quota.domain.service';

import { NewRouteViewModel } from '../new-route.viewModel';
import { Job } from '../../domain/models/job';
import { Schedule } from '../../domain/models/schedule';
import { Position } from '../../domain/models/position';
import { PositionType } from '../../domain/enums/positionType';
import { Size } from '../../domain/models/size';
import { JobType } from '../../domain/enums/jobType';
import { UserData } from '../../domain/models/userData';
import { CalculateQuotaRequest, CalculateQuotaResponse } from '../../domain/models/calculateQuota';
import { Project } from '../../domain/models/project';
import { RouteDraft } from '../../domain/models/routeDraft';

import * as _ from 'lodash';
import { Polyline } from '../../domain/models/polyline';

@Injectable()
export class NewRouteService {

  private startPoint: any;
  private endPoint: any;
  private waypoints: Array<any> = [];

  constructor(
    private mapsService: MapsService,
    private jobDomainService: JobDomainService,
    private userDomainService: UserDomainService,
    private quotaDomainService: QuotaDomainService) { }

  saveNewRoute(routeData: NewRouteViewModel): Observable<string> {
    let job = new Job();
    job.name = routeData.name;
    job.start = new Date(routeData.startDate);
    job.end = new Date(routeData.endDate);
    job.schedule = new Schedule(routeData.startTime, routeData.endTime, routeData.interval, routeData.days);
    job.locations = this.mapWaypoints(routeData.route);
    job.minimumSectionLength = routeData.roadsegment;
    job.startAddress = this.getStartAddress(routeData.route);
    job.endAddress = this.getEndAddress(routeData.route);
    job.jobType = this.setJobType(routeData);
    job.projectId = routeData.projectId;

    return this.jobDomainService.createJob(job);
  }

  private setJobType(newRouteVm: NewRouteViewModel): JobType {
    let jobType = JobType.LineGraph;
    if (newRouteVm.includeHistoricalData) {
      jobType |= JobType.Historical;
    }
    if (newRouteVm.calculateSpectrogram) {
      jobType |= JobType.Spectrogram;
    }
    if (newRouteVm.calculateBudgetSpectrogram) {
      jobType |= JobType.BudgetSpectrogram;
    } if (newRouteVm.branchingAnalyse) {
      jobType |= JobType.BranchingAnalyse;
    }
    return jobType;
  }

  setStartPoint(startPoint: any, markerUrl: string, size: Size): void {
    this.startPoint = startPoint;
    this.drawRoute(markerUrl, size);
  }

  setEndPoint(endPoint: any, markerUrl: string, size: Size): void {
    this.endPoint = endPoint;
    this.drawRoute(markerUrl, size);
  }

  startPointIsSet(): boolean {
    return this.startPoint !== undefined && this.startPoint !== null;
  }

  endPointIsSet(): boolean {
    return this.endPoint !== undefined && this.endPoint !== null;
  }

  measure(lat1: number, lon1: number, lat2: number, lon2: number): number {
    let R = 6378.137; // Radius of earth in KM
    let dLat = lat2 * Math.PI / 180 - lat1 * Math.PI / 180;
    let dLon = lon2 * Math.PI / 180 - lon1 * Math.PI / 180;
    let a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    let d = R * c;
    return d * 1000; // meters
  }

  drawRoute(markerUrl: string, size: Size): void {
    if (this.startPoint && this.endPoint) {
      this.mapsService.drawRoute(this.startPoint, this.endPoint, this.waypoints);
    } else if (this.startPoint) {
      this.mapsService.setTempMarker(markerUrl, size, this.startPoint);
    }
  }
  setRoute(locations: any[]) {
    let waypointsList = [];
    if (locations) {
      locations.forEach(wp => {
        waypointsList.push({ lat: wp.y, lng: wp.x });
      });
    }
    let startPoint = waypointsList.shift();
    this.startPoint = startPoint;
    let endpoint = waypointsList.pop();
    this.endPoint = endpoint;
    this.mapsService.drawRoute(startPoint, endpoint, waypointsList);
  }

  drawOtherRoutes(jobs: Job[]): void {
    let routes = [];
    _.each(jobs, (job: Job) => {
      let locations = _.cloneDeep(job.locations);
      let start = locations.shift();
      let end = locations.pop();

      routes.push(new Polyline({ id: job.id, name: job.name, startpoint: start, endpoint: end, waypoints: locations }));
    });
    this.mapsService.drawPolyLinesForRoutes(routes);
  }

  clearOtherRoutes(): void {
    this.mapsService.clearMapOverlays(true);
  }

  clearCachedPolyLines(): void {
    this.mapsService.clearCachedPolyLines();
  }

  drawRouteWithWayPoints(waypoints: any) {
    this.mapsService.drawRoute(this.startPoint, this.endPoint, waypoints);
  }

  getCurrentRoute(): any {
    return this.mapsService.getCurrentRoute();
  }

  getRouteLength(): number {
    return this.mapsService.getCurrentRouteLength();
  }

  clearRoute(): void {
    this.mapsService.clearRoute();
    this.waypoints = [];
    this.startPoint = undefined;
    this.endPoint = undefined;
  }

  getDirectionsChangedObservable(): Observable<any> {
    return this.mapsService.getDirectionsChangedObservable();
  }

  reverseGeocode(latlng: any): Promise<any> {
    return this.mapsService.reverseGeoCode(latlng);
  }

  getClickEventObservable(): Observable<any> {
    return this.mapsService.getClickEventObservable();
  }

  mapWaypoints(route: any): Position[] {
    let leg = route.routes[0].legs[0];
    let positions: Position[] = [];
    positions.push(new Position(PositionType.Origin, leg.start_location.lng(), leg.start_location.lat()));

    leg.via_waypoints.forEach(wp => {
      positions.push(new Position(PositionType.Waypoint, wp.lng(), wp.lat()));
    });
    positions.push(new Position(PositionType.Destination, leg.end_location.lng(), leg.end_location.lat()));

    return positions;
  }

  private getStartAddress(route: any): string {
    let leg = route.routes[0].legs[0];
    return leg.start_address;
  }

  private getEndAddress(route: any): string {
    let leg = route.routes[0].legs[0];
    return leg.end_address;
  }

  getUserSettings(): Promise<UserData> {
    return this.userDomainService.getUserData();
  }

  updateUserSettings(userData: UserData): void {
    this.userDomainService.updateUserData(userData);
  }

  focusSearch(): void {
    this.mapsService.focusSearch();
  }

  enableSearch(): void {
    this.mapsService.enableSearch();
  }
  disableSearch(): void {
    this.mapsService.disableSearch();
  }

  calculateQuota(request: CalculateQuotaRequest): Promise<CalculateQuotaResponse> {
    return this.quotaDomainService.calculateQuota(request).toPromise();
  }

  getJobStatus(jobId: string): Observable<{} | string> {
    return this.jobDomainService.getJobStatus(jobId);
  }

  getProjectById(projectId: string): Observable<{} | Project> {
    return this.jobDomainService.getProjectById(projectId);
  }

  getJobById(jobId: string): Observable<void | Job> {
    return this.jobDomainService.getJob(jobId);
  }

  getRouteDraftFromLocalStorage(jobId: string): RouteDraft {
    return this.jobDomainService.getRouteDraftFromLocalStorage(jobId);
  }

  deleteRouteDraftFromLocalStorage(jobId: string): void {
    this.jobDomainService.deleteRouteDraftFromLocalStorage(jobId);
  }

  countDays(startDate: Date, endDate: Date, selectedDays: number[]): Date[] {
    let result = [];
    for (let i = new Date(startDate); i <= endDate; i.setDate(i.getDate() + 1)) {
      let hasDay = selectedDays.find((day: number) => {
        return day === i.getDay();
      });
      if (hasDay !== undefined && hasDay >= 0) {
        let date = new Date(i);
        date.setHours(date.getHours() - (date.getTimezoneOffset() / 60));
        result.push(date);
      }
    }
    return result;
  }

  calculateRequestsPerDay(startTime: Date, endTime: Date, interval: number): number {
    let diffMs = endTime.getTime() - startTime.getTime();
    let diffMin = Math.floor((diffMs / 1000) / 60);
    return Math.round(diffMin / interval);
  }

  calculateHistoricalDays(startDate: Date, endDate: Date, selectedDays: number[]): number {
    let count = 0;

    selectedDays.forEach((day: number) => {
      let date = new Date(startDate);
      let found = false;
      while (date <= endDate && !found) {
        if (date.getDay() === day) {
          found = true;
        }
        date.setDate(date.getDate() + 1);
      }
      if (found) {
        count++;
      }
    });

    return count;
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/new-route/services/new-route.service.ts